import { ReactNode, useContext, useEffect, useState } from 'react'
import EmissionService from '../../../../shared/services/emission/emission.service'
import EmissionCalculationService from '../../../../shared/services/emission-calculation/emission-calculation.service'
import {
  EmissionDetailLocationState,
  IEmissionDetail,
  IEmissionDetailHeaderPreview,
} from '../../../../shared/services/emission/emission.model'
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container'
import { BackButtonLink } from '../../../presentation-components/display-elements-components/back-button-link'
import { LoadingSpinner } from '../../../presentation-components/display-elements-components/loading-spinner'
import { AutoAwesomeOutlined, DeleteOutline, EditOutlined } from '@mui/icons-material'
import { Alert, Box, Button, Chip, Link as MuiLink, Skeleton, Stack, Tab, Tabs, Typography } from '@mui/material'
import { a11yProps } from '../../../../shared/utils/misc-shared-utils'
import { CustomTabPanel } from '../../../presentation-components/display-elements-components/custom-tab-panel'
import { ShallowFormDisplay } from '../../../presentation-components/form-field-components/general/shallow-form-display'
import moment from 'moment'
import { EmissionCalculation } from '../../../presentation-components/emissons-components/overview/emission-calulation'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import { UserDetailsContext } from '../../../../context/user-details/user-details'
import { EditEmissionDialog } from '../../../dialogs/emissions/edit-emission-dialog'
import { EditEmissionCalculationDialog } from '../../../dialogs/emissions/edit-emission-calculation-dialog'
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels'
import { EmissionDetailTable } from '../../../presentation-components/emissons-components/overview/emission-detail-table'
import { EmissionAuditTrail } from '../../../presentation-components/emissons-components/overview/emission-audit-trail'
import { EmissionEvidencePanel } from '../../../presentation-components/emissons-components/overview/emission-evidence-panel'
import { useIsReportPeriodLocked } from '../../../../hooks/use-is-report-period-locked'
import { formatDataQualityTierLabel, formatEmissionPeriodRange, formatEmissionTonnes } from '../../../../shared/utils/emission-summary-utils'
import LocationService from '../../../../shared/services/location/location.service'
import SubsidiaryService from '../../../../shared/services/subsidiary/subsidiary.service'
import { IEmissionCalculation } from '../../../../shared/services/emission-calculation/emission-calculation.model'
import AiCopilotService from '../../../../shared/services/ai-copilot/ai-copilot.service'
import { IChatTurn } from '../../../../shared/services/ai-copilot/ai-copilot.model'
import { CopilotPanel } from '../../../copilot/CopilotPanel'
import { NotesPanel } from '../../../presentation-components/notes-components/notes-panel'

function displayValue(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return '—'
  const str = String(value).trim()
  return str.length === 0 ? '—' : str
}

function linkedDisplayValue(value: string | number | undefined | null, to?: string): ReactNode {
  const label = displayValue(value)
  if (!to || label === '—') return label
  return (
    <MuiLink
      component={RouterLink}
      to={to}
      sx={{
        color: 'primary.main',
        textDecoration: 'underline',
        textUnderlineOffset: '2px',
        '&:hover': {
          color: 'primary.dark',
        },
      }}
    >
      {label}
    </MuiLink>
  )
}

function coerceBool(value: unknown): boolean {
  if (value === true || value === 1) return true
  if (value === false || value === 0) return false
  if (value === null || value === undefined) return false
  if (typeof value === 'string') {
    const s = value.toLowerCase().trim()
    if (s === 'true' || s === '1') return true
    if (s === 'false' || s === '0' || s === '') return false
  }
  return Boolean(value)
}

type HeaderDisplaySource = Pick<
  IEmissionDetailHeaderPreview,
  'activityLabel' | 'activityContext' | 'totalEmissionsKg' | 'methodLabel' | 'primaryDataPercent' | 'startDate' | 'endDate'
>

function EmissionDetailHeaderSummaryRow({
  source,
  isGreyMethod,
}: {
  source: HeaderDisplaySource
  isGreyMethod: boolean
}) {
  return (
    <Stack
      direction='row'
      flexWrap='wrap'
      alignItems='center'
      gap={2}
      sx={{ mb: 1.5 }}
    >
      <Typography variant='body1' sx={{ fontWeight: 800 }}>
        {formatEmissionTonnes(source.totalEmissionsKg)}
      </Typography>
      {source.methodLabel ? (
        <Chip
          size='small'
          label={source.methodLabel}
          color={isGreyMethod ? 'primary' : 'default'}
          variant={isGreyMethod ? 'outlined' : 'filled'}
          sx={{
            width: 128,
            ...(isGreyMethod
              ? source.methodLabel === 'Spend-based'
                ? { borderColor: '#9e9e9e', color: '#757575' }
                : { borderColor: '#9e9e9e', color: '#616161' }
              : {
                  bgcolor: 'secondary.light',
                  color: 'primary.dark',
                  border: '1px solid',
                  borderColor: 'primary.main',
                }),
          }}
        />
      ) : (
        <Typography variant='body2'>—</Typography>
      )}
      {typeof source.primaryDataPercent === 'number' ? (
        <Chip
          size='small'
          variant='outlined'
          color='primary'
          label={formatDataQualityTierLabel(source.primaryDataPercent)}
          sx={{ borderColor: 'primary.main', color: 'primary.dark', bgcolor: 'transparent' }}
        />
      ) : (
        <Typography variant='body2'>—</Typography>
      )}
      <Typography variant='body2' sx={{ color: 'text.primary' }}>
        {formatEmissionPeriodRange(source.startDate, source.endDate)}
      </Typography>
    </Stack>
  )
}

function TabContentLoading() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, py: 4 }}>
      <LoadingSpinner />
    </Box>
  )
}

export const EmissionDetail = () => {
  const { emissionId } = useParams()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const userNotificationsContext = useContext(UserNotificationsContext)
  const userDetailsContext = useContext(UserDetailsContext)
  const userDetails = userDetailsContext.userDetails
  const screenLabelsContext = useContext(ScreenLabelsContext)
  const isReportPeriodLocked = useIsReportPeriodLocked()
  const emissionService = new EmissionService()
  const emissionCalculationService = new EmissionCalculationService()
  const aiCopilotService = new AiCopilotService()
  const [emission, setEmission] = useState<IEmissionDetail>()
  const [isLoading, setIsLoading] = useState(() => Boolean(emissionId))
  const [loadError, setLoadError] = useState<string | null>(null)
  const [value, setValue] = useState(0)
  const [openEditDialog, setOpenEditDialog] = useState(false)
  const [openEditCalculationDialog, setOpenEditCalculationDialog] = useState(false)
  const [selectedEditCalculation, setSelectedEditCalculation] = useState<IEmissionCalculation | null>(null)
  const [selectedEditCalculationIds, setSelectedEditCalculationIds] = useState<string[]>([])
  const [copilotOpen, setCopilotOpen] = useState(false)

  const backLink = searchParams.get('backLink') ?? '/emissions/overview/table'
  const backText = searchParams.get('backText') ?? 'Back to emissions'
  const [showEmissionCalculation, setShowEmissionCalculation] = useState(false)
  const [selectedCalculationIds, setSelectedCalculationIds] = useState<string[]>([])
  // Data owners write like scoped analysts (the BE emissionWriteAccess treats them identically).
  const canManageEmissions = !!userDetails?.roles?.some(role => role === 'admin' || role === 'analyst' || role === 'data-owner')
  const roles = userDetails?.roles || []
  const isAdmin = roles.includes('admin')
  const isScopedWriter = (roles.includes('analyst') || roles.includes('data-owner')) && !isAdmin
  const locationService = LocationService.getInstance()
  const subsidiaryService = SubsidiaryService.getInstance()
  const [scopeCacheLoaded, setScopeCacheLoaded] = useState(() => {
    if (!isScopedWriter) return true
    return Boolean(locationService.locations) && Boolean(subsidiaryService.subsidiaries)
  })

  const allowedLocationIds = locationService.locations?.map((l) => l.id)
  const allowedSubsidiaryIds = subsidiaryService.subsidiaries?.map((s) => s.id)

  useEffect(() => {
    if (!isScopedWriter) return
    Promise.allSettled([locationService.getAllLocations(), subsidiaryService.getAll()])
      .then(() => setScopeCacheLoaded(true))
      .catch(() => setScopeCacheLoaded(true))
  }, [isScopedWriter])

  const hasScopeWriteAccess = !isScopedWriter || !emission || (() => {
    if (!scopeCacheLoaded) return false
    if (emission.locationId && Array.isArray(allowedLocationIds) && !allowedLocationIds.includes(emission.locationId)) return false
    if (emission.subsidiaryId && Array.isArray(allowedSubsidiaryIds) && !allowedSubsidiaryIds.includes(emission.subsidiaryId)) return false
    return true
  })()

  const canEditOrDeleteEmission = canManageEmissions && !isReportPeriodLocked && hasScopeWriteAccess
  // Notes explain a figure and never change one, so they stay writable on a locked period. Clients
  // have no notes access in v1, so the tab is not shown to them.
  const canWriteNotes = canManageEmissions && hasScopeWriteAccess
  // Copilot is not opened to data owners (its context is org-wide and not scope-aware).
  const canUseCopilot = !!userDetails?.organisation?.aiCopilotEnabled && (isAdmin || roles.includes('analyst') || roles.includes('client'))

  const emissionPreview = (location.state as EmissionDetailLocationState | null | undefined)?.emissionPreview
  const previewMatches = !!(emissionPreview && emissionId && emissionPreview.id === emissionId)

  const headerSource: HeaderDisplaySource | undefined = emission ?? (previewMatches ? emissionPreview : undefined)
  const showHeaderSkeleton = isLoading && !headerSource
  const activityTitle = headerSource?.activityLabel?.split(' · ')[0]?.trim() || 'Emission'
  const activitySubtitle = headerSource?.activityContext?.trim() || ''
  const showTitleBlock = showHeaderSkeleton || headerSource

  const isGreyMethod =
    headerSource?.methodLabel === 'Spend-based' || headerSource?.methodLabel === 'Hybrid'

  const tabContentLoading = isLoading && !emission

  useEffect(() => {
    if (!emissionId) return
    setEmission(undefined)
    setLoadError(null)
    setShowEmissionCalculation(false)
    setSelectedCalculationIds([])
    setIsLoading(true)
    getEmission(emissionId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emissionId])

  const getEmission = async (id: string) => {
    setIsLoading(true)
    setLoadError(null)
    try {
      const data = await emissionService.getOne(id)
      setEmission(data)
    } catch (error) {
      console.error(error)
      setLoadError('Unable to load this emission. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const changeTabHandler = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }

  // Label of the family the backend re-selected after a delete uncovered a superseded one.
  // Reads the pre-refresh calculations, so the restored rows are still on the record here.
  const restoredCalculationMethodLabel = (restoredIds: string[]): string | null => {
    const calcs = emission?.emissionCalculations ?? []
    const restored = calcs.filter((c) => restoredIds.includes(c.id))
    const primary = restored.find((c) => !c.isWtt) ?? restored[0]
    if (!primary) return null
    switch (primary.type) {
      case 'SPEND': return 'spend-based'
      case 'ACTIVITY': return 'activity-based'
      case 'PRODUCT': return 'product-based'
      case 'DIRECT': return 'direct'
      default: return null
    }
  }

  const deleteCalculationsHandler = (ids: string[], isLastRecord: boolean) => {
    const message = isLastRecord
      ? 'This is the only calculation for this emission. Are you sure you want to delete it? This will delete the entire emission and cannot be undone.'
      : 'Are you sure you want to delete this emission calculation? This action cannot be undone.'

    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message,
      buttonCopy: 'Continue',
      cancelButtonCopy: 'Cancel',
    }).then(confirmed => {
      if (confirmed) {
        const deleteAction = isLastRecord && emissionId
          ? emissionService.deleteEmissions([emissionId]).then(() => undefined)
          : emissionCalculationService.deleteEmissionCalculations(ids)

        deleteAction
          .then((result) => {
            const restoredIds = result && 'restoredCalculationIds' in result ? result.restoredCalculationIds ?? [] : []
            const restoredLabel = restoredIds.length ? restoredCalculationMethodLabel(restoredIds) : null
            const message = restoredLabel
              ? `Deleted. The ${restoredLabel} calculation is used in reports again.`
              : 'Deleted successfully'
            userNotificationsContext.openSnackbar({ open: true, message })
            if (isLastRecord) {
              navigate(backLink)
            } else if (emissionId) {
              getEmission(emissionId).catch(console.error)
            }
          })
          .catch(err => {
            console.log(err)
            const message = err?.response?.data?.message || 'There was an error, please try again'
            userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
          })
      }
    })
  }

  const deleteHandler = () => {
    if (!emissionId) return
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: 'Are you sure you want to delete this emission? This action cannot be undone.',
      buttonCopy: 'Continue',
      cancelButtonCopy: 'Cancel',
    }).then(confirmed => {
      if (confirmed) {
        emissionService.deleteEmissions([emissionId])
          .then(() => {
            userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' })
            navigate(backLink)
          })
          .catch(err => {
            console.log(err)
            const message = err?.response?.data?.message || 'There was an error, please try again'
            userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
          })
      }
    })
  }

  const buildCalculationEditIds = (targetCalculation: IEmissionCalculation): string[] => {
    const allCalculations = emission?.emissionCalculations ?? []
    const ids = new Set<string>([targetCalculation.id])

    if (targetCalculation.type === 'SPEND') {
      allCalculations
        .filter((calculation) =>
          calculation.type === 'MEASUREMENT' &&
          calculation.calendarYear === targetCalculation.calendarYear &&
          calculation.scope === targetCalculation.scope,
        )
        .forEach((calculation) => ids.add(calculation.id))
    }

    if (targetCalculation.type === 'ACTIVITY' && !coerceBool(targetCalculation.isWtt)) {
      allCalculations
        .filter((calculation) =>
          calculation.calendarYear === targetCalculation.calendarYear &&
          calculation.scope === targetCalculation.scope &&
          (
            calculation.type === 'MEASUREMENT' ||
            (calculation.type === 'ACTIVITY' && coerceBool(calculation.isWtt))
          ),
        )
        .forEach((calculation) => ids.add(calculation.id))

      if (targetCalculation.scope === '2') {
        allCalculations
          .filter((calculation) =>
            calculation.type === 'ACTIVITY' &&
            calculation.calendarYear === targetCalculation.calendarYear &&
            calculation.scope === targetCalculation.scope &&
            !coerceBool(calculation.isWtt) &&
            coerceBool(calculation.isMarketBased) !== coerceBool(targetCalculation.isMarketBased),
          )
          .forEach((calculation) => ids.add(calculation.id))
      }
    }

    return [...ids]
  }

  if (!emissionId) {
    return null
  }

  return (
    <div>
      <PageOuterContainer>
        <Box sx={{ mb: 1 }}>
          <BackButtonLink buttonText={backText} link={backLink} />
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent='space-between'
          alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Box>
            {showHeaderSkeleton ? (
              <Box sx={{ width: '100%', maxWidth: 480 }}>
                <Skeleton
                  variant='rounded'
                  height={40}
                  sx={{
                    width: { xs: '100%', sm: 380 },
                    maxWidth: '100%',
                    borderRadius: 1,
                  }}
                />
                <Skeleton
                  variant='rounded'
                  height={24}
                  sx={{
                    width: { xs: '75%', sm: 240 },
                    maxWidth: '100%',
                    mt: 0.75,
                    borderRadius: 1,
                  }}
                />
              </Box>
            ) : showTitleBlock ? (
              <>
                <Typography variant='h4' component='h1' sx={{ fontWeight: 500, color: 'text.primary' }}>
                  {activityTitle}
                </Typography>
                {activitySubtitle && (
                  <Typography variant='body1' sx={{ color: 'text.secondary', mt: 0.25 }}>
                    {activitySubtitle}
                  </Typography>
                )}
              </>
            ) : null}
          </Box>
          {(canUseCopilot || canEditOrDeleteEmission) && (
            <Stack direction='row' spacing={1} sx={{ flexShrink: 0 }}>
              {canUseCopilot && (
                <Button
                  variant='outlined'
                  startIcon={<AutoAwesomeOutlined fontSize='small' />}
                  onClick={() => setCopilotOpen(true)}
                >
                  <Typography sx={{ pl: 0.5 }}>Explain this emission</Typography>
                </Button>
              )}
              {canEditOrDeleteEmission && (
                <>
                  <Button
                    variant='contained'
                    startIcon={<EditOutlined fontSize='small' />}
                    onClick={() => setOpenEditDialog(true)}
                  >
                    <Typography sx={{ pl: 0.5 }}>Edit Emission</Typography>
                  </Button>
                  <Button
                    variant='outlined'
                    startIcon={<DeleteOutline fontSize='small' />}
                    onClick={deleteHandler}
                  >
                    <Typography sx={{ pl: 0.5 }}>Delete Emission</Typography>
                  </Button>
                </>
              )}
            </Stack>
          )}
        </Stack>
        {showHeaderSkeleton ? (
          <Stack direction='row' flexWrap='wrap' alignItems='center' gap={2} sx={{ mb: 1.5 }}>
            <Skeleton variant='rounded' width={120} height={28} />
            <Skeleton variant='rounded' width={128} height={28} />
            <Skeleton variant='rounded' width={200} height={28} />
            <Skeleton variant='text' width={140} />
          </Stack>
        ) : (
          headerSource && (
            <EmissionDetailHeaderSummaryRow source={headerSource} isGreyMethod={!!isGreyMethod} />
          )
        )}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={changeTabHandler}>
            <Tab label='Details' {...a11yProps(0)} />
            <Tab label='Calculations' {...a11yProps(1)} />
            <Tab label='Audit Trail' {...a11yProps(2)} />
            <Tab label='Evidence' {...a11yProps(3)} />
            {canManageEmissions && <Tab label='Notes' {...a11yProps(4)} />}
          </Tabs>
        </Box>
        {loadError && !emission && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {loadError}
          </Alert>
        )}
        <CustomTabPanel value={value} index={0}>
          {tabContentLoading ? (
            <TabContentLoading />
          ) : emission ? (
            <>
              <ShallowFormDisplay small={true} isTopRow={true} name={screenLabelsContext.getLabel('organisation', { plural: false, capitalize: 'first' })} value={displayValue(emission.organisation.name)} />
              <ShallowFormDisplay small={true} name='Report Period' value={linkedDisplayValue(emission.reportPeriod.calendarYear, `/settings/company-settings/reporting-periods/${emission.reportPeriod.id}`)} />
              <ShallowFormDisplay small={true} name='Start Date' value={displayValue(moment(emission.startDate).format('DD/MM/YYYY'))} />
              <ShallowFormDisplay small={true} name='End Date' value={displayValue(moment(emission.endDate).format('DD/MM/YYYY'))} />
              <ShallowFormDisplay small={true} name={screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })} value={linkedDisplayValue(emission.subsidiary.name, emission.subsidiaryId ? `/organisation/subsidiaries/${emission.subsidiaryId}` : undefined)} />
              <ShallowFormDisplay small={true} name='Country' value={displayValue(emission.country.name)} />
              <ShallowFormDisplay small={true} name={screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' })} value={linkedDisplayValue(emission.location?.friendlyName, emission.locationId ? `/organisation/locations/${emission.locationId}` : undefined)} />
              <ShallowFormDisplay small={true} name={screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} value={linkedDisplayValue(emission.vehicle?.customUniqueId, emission.vehicleId ? `/organisation/vehicles/${emission.vehicleId}` : undefined)} />
              <ShallowFormDisplay small={true} name={screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' })} value={linkedDisplayValue(emission.project?.summary, emission.projectId ? `/organisation/projects/${emission.projectId}` : undefined)} />
              <ShallowFormDisplay small={true} name={screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' })} value={linkedDisplayValue(emission.contactSupplier?.name, emission.contactSupplierId ? `/organisation/suppliers/${emission.contactSupplierId}` : undefined)} />
              <ShallowFormDisplay small={true} name={screenLabelsContext.getLabel('product', { plural: false, capitalize: 'first' })} value={linkedDisplayValue(emission.capitalGood?.name, emission.capitalGoodId ? `/organisation/products/${emission.capitalGoodId}` : undefined)} />
              <ShallowFormDisplay
                small={true}
                name='Emission Trading Scheme'
                value={displayValue(emission.regulatedEmissionTradingScheme?.name)}
              />
              {emission.customFieldValues.map((customFieldValue) => (
                <ShallowFormDisplay small={true} key={customFieldValue.id} name={customFieldValue.customField.fieldName} value={displayValue(customFieldValue.value)} />
              ))}
              <ShallowFormDisplay small={true} isBottomRow={!emission.excludeFromReporting} name='Exclude From Reporting' value={emission.excludeFromReporting ? 'Yes' : 'No'} />
              {emission.excludeFromReportingReason && <ShallowFormDisplay small={true} isBottomRow={true} name='Exclude From Reporting Reason' value={emission.excludeFromReportingReason} />}
            </>
          ) : null}
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          {tabContentLoading ? (
            <TabContentLoading />
          ) : emission ? (
            <>
              {!showEmissionCalculation &&
                <EmissionDetailTable
                  key={`emission-table-${emission.id}-${(emission.emissionCalculations ?? []).map(c => c.id).sort().join(',')}`}
                  calculations={emission.emissionCalculations || []}
                  onView={(ids) => {
                    setSelectedCalculationIds(ids)
                    setShowEmissionCalculation(true)
                  }}
                  onDelete={canEditOrDeleteEmission ? deleteCalculationsHandler : undefined}
                  onAddSuccess={() => emissionId && getEmission(emissionId)}
                  emission={emission}
                />
              }
              {showEmissionCalculation &&
                <EmissionCalculation
                  ids={selectedCalculationIds}
                  onBack={() => setShowEmissionCalculation(false)}
                  canEdit={canEditOrDeleteEmission}
                  onEdit={(calculation) => {
                    setSelectedEditCalculation(calculation)
                    setSelectedEditCalculationIds(buildCalculationEditIds(calculation))
                    setOpenEditCalculationDialog(true)
                  }}
                />
              }
            </>
          ) : null}
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          {value === 2 && <EmissionAuditTrail emissionId={emissionId} />}
        </CustomTabPanel>
        <CustomTabPanel value={value} index={3}>
          {value === 3 && <EmissionEvidencePanel emissionId={emissionId} canEdit={canEditOrDeleteEmission} />}
        </CustomTabPanel>
        {canManageEmissions && (
          <CustomTabPanel value={value} index={4}>
            {value === 4 && (
              tabContentLoading ? (
                <TabContentLoading />
              ) : (
                <NotesPanel
                  relatedModelName='emission'
                  relatedObjectId={emissionId}
                  reportPeriodId={emission?.reportPeriodId}
                  canWrite={canWriteNotes}
                  systemEntries={emission?.excludeFromReportingReason
                    ? [{ label: 'Reason for exclusion', text: emission.excludeFromReportingReason }]
                    : []}
                />
              )
            )}
          </CustomTabPanel>
        )}
        {emissionId && canEditOrDeleteEmission && (
          <EditEmissionDialog
            open={openEditDialog}
            dialogDetailsId={emissionId}
            closeHandler={() => setOpenEditDialog(false)}
            successHandler={() => {
              getEmission(emissionId).catch(console.error)
              setOpenEditDialog(false)
            }}
          />
        )}
        {emission && selectedEditCalculation && (
          <EditEmissionCalculationDialog
            open={openEditCalculationDialog}
            calculation={selectedEditCalculation}
            calculationIds={selectedEditCalculationIds}
            calculations={emission.emissionCalculations || []}
            emission={emission}
            closeHandler={() => {
              setOpenEditCalculationDialog(false)
              setSelectedEditCalculation(null)
              setSelectedEditCalculationIds([])
            }}
            successHandler={(emissionCalculationIds) => {
              if (emissionId) {
                getEmission(emissionId).catch(console.error)
              }
              setOpenEditCalculationDialog(false)
              setSelectedEditCalculation(null)
              setSelectedEditCalculationIds([])
              if (emissionCalculationIds?.length) {
                setSelectedCalculationIds(emissionCalculationIds)
                setShowEmissionCalculation(true)
              } else {
                setShowEmissionCalculation(false)
                setSelectedCalculationIds([])
              }
            }}
          />
        )}
        {canUseCopilot && (
          <CopilotPanel
            open={copilotOpen}
            title='Explain this emission'
            onClose={() => setCopilotOpen(false)}
            request={() => aiCopilotService.explainEmission({ emissionId: emissionId || '' })}
            followUpRequest={(userQuestion: string, history?: IChatTurn[]) => aiCopilotService.explainEmission({ emissionId: emissionId || '', userQuestion, history })}
            suggestedActions={[
              { label: 'Why is this high?', question: 'Why is this high?' },
              { label: 'How can I improve this data?', question: 'How can I improve this data?' },
              { label: 'What calculation method was used?', question: 'What calculation method was used?' },
            ]}
          />
        )}
      </PageOuterContainer>
    </div>
  )
}
